'use client'

import { createContext, useContext, useState, useEffect } from 'react'

interface CartItem {
  variantId: string
  quantity: number
  name: string
  price: string
  image: string
}

interface CartContextType {
  items: CartItem[]
  itemCount: number
  addItem: (item: CartItem) => void
  removeItem: (variantId: string) => void
  updateItemQuantity: (variantId: string, quantity: number) => void
  clearCart: () => void
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])
  
  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart')
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        // Validate prices of loaded cart items
        const validatedCart = parsedCart.map((item: CartItem) => {
          let price = '0.00'
          if (item.price !== undefined && item.price !== null) {
            const parsedPrice = typeof item.price === 'string' 
              ? parseFloat(item.price) 
              : Number(item.price)
            
            price = isNaN(parsedPrice) ? '0.00' : parsedPrice.toFixed(2)
          }
          return { ...item, price }
        });
        setItems(validatedCart);
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        setItems([]);
      }
    }
  }, [])

  // Save cart to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items))
  }, [items])

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)

  const addItem = (newItem: CartItem) => {
    // Validate and ensure price is a formatted string with 2 decimal places
    let price = '0.00'
    
    // Handle different price formats and invalid values
    if (newItem.price !== undefined && newItem.price !== null) {
      const parsedPrice = typeof newItem.price === 'string' 
        ? parseFloat(newItem.price) 
        : Number(newItem.price)
      
      price = isNaN(parsedPrice) ? '0.00' : parsedPrice.toFixed(2)
    }
    
    const formattedItem = {
      ...newItem,
      price
    }

    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.variantId === formattedItem.variantId)
      
      if (existingItem) {
        return currentItems.map(item =>
          item.variantId === formattedItem.variantId
            ? { ...item, quantity: item.quantity + formattedItem.quantity }
            : item
        )
      }
      
      return [...currentItems, formattedItem]
    })
  }

  const removeItem = (variantId: string) => {
    setItems(currentItems => currentItems.filter(item => item.variantId !== variantId))
  }

  const updateItemQuantity = (variantId: string, quantity: number) => {
    setItems(currentItems => {
      if (quantity <= 0) {
        return currentItems.filter(item => item.variantId !== variantId)
      }
      
      return currentItems.map(item =>
        item.variantId === variantId
          ? { ...item, quantity }
          : item
      )
    })
  }

  const clearCart = () => {
    setItems([])
  }

  return (
    <CartContext.Provider value={{
      items,
      itemCount,
      addItem,
      removeItem,
      updateItemQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
} 